import React, { useEffect, useRef, useState } from "react";
import api from "../api";
import { Save, Image as ImageIcon, Upload, Send, RotateCcw } from "lucide-react";

const DEFAULT_SIGNATURE =
  "<strong>Max Valle</strong> &middot; AI &amp; Internet Business Specialist dal 1993<br>" +
  "33 anni di esperienza, oltre 2.500 imprese italiane seguite.<br>" +
  "Numero Verde 800 180 440 &middot; clienti@maxvalle.it &middot; " +
  '<a href="https://maxvalle.it" style="color:#c95a1a">maxvalle.it</a>';

const FIELDS = [
  ["brandSenderName", "Nome mittente", "Max Valle", "brand-sender-name"],
  ["brandReplyTo", "Indirizzo di risposta (reply-to)", "clienti@maxvalle.it", "brand-reply-to"],
  ["brandPhone", "Numero verde / telefono", "800 180 440", "brand-phone"],
  ["brandWebsite", "Sito mostrato nei recapiti", "maxvalle.it", "brand-website"],
  ["brandPrivacyUrl", "Link Privacy Policy", "https://maxvalle.it/privacy-policy/", "brand-privacy-url"],
  ["brandCalendarUrl", "Link calendario (prenotazione call)", "https://calendar.app.google/…", "brand-calendar-url"],
  ["brandPdfFooterName", "Ragione sociale nel piè di pagina del PDF", "Max Valle · It's Genius Srl", "brand-pdf-footer"],
];

export default function BrandingSettings() {
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/settings").then(r => {
      const s = r.data || {};
      setState({
        brandLogoUrl: s.brandLogoUrl || "",
        brandSenderName: s.brandSenderName || "Max Valle",
        brandReplyTo: s.brandReplyTo || "clienti@maxvalle.it",
        brandPhone: s.brandPhone || "800 180 440",
        brandWebsite: s.brandWebsite || "maxvalle.it",
        brandPrivacyUrl: s.brandPrivacyUrl || "https://maxvalle.it/privacy-policy/",
        brandCalendarUrl: s.brandCalendarUrl || "",
        brandPdfFooterName: s.brandPdfFooterName || "Max Valle · It's Genius Srl",
        brandSignatureHtml: s.brandSignatureHtml || DEFAULT_SIGNATURE,
      });
      setTestTo(s.brandReplyTo || "");
    });
  }, []);

  if (!state) return null;

  const upd = (k, v) => setState(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      await api.put("/settings", state);
      setMsg({ ok: true, text: "Branding salvato: email e PDF usano questi valori." });
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true); setMsg(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/settings/branding/logo", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      const next = { ...state, brandLogoUrl: data.url };
      setState(next);
      await api.put("/settings", next);
      setMsg({ ok: true, text: "Logo caricato e salvato." });
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || e.message });
    } finally { setUploading(false); }
  };

  const sendTest = async () => {
    setSending(true); setMsg(null);
    try {
      await api.put("/settings", state);
      const { data } = await api.post("/settings/branding/test-email", { to: testTo });
      setMsg({ ok: true, text: `Email di test inviata a ${data.to}.` });
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || e.message });
    } finally { setSending(false); }
  };

  return (
    <div className="card mb-4" data-testid="branding-settings">
      <div className="flex items-center gap-2 mb-2">
        <ImageIcon size={18} color="var(--mv-primary)" />
        <div className="h-section" style={{ margin: 0 }}>Logo, firma e recapiti delle email</div>
      </div>
      <p className="text-small text-muted mb-4">
        Questi valori compaiono in <strong>tutte le email</strong> (T1, T2, T3 e risposta
        automatica inbound) e nel <strong>PDF del report</strong>. Modificali quando vuoi:
        valgono dal prossimo invio.
      </p>

      {/* ---------------- LOGO ---------------- */}
      <div className="card mb-4" style={{ padding: 14, background: "var(--mv-bg)" }}>
        <div className="text-small" style={{ fontWeight: 700, marginBottom: 8 }}>Logo</div>
        <div className="flex gap-4 items-start" style={{ flexWrap: "wrap" }}>
          <div style={{
            background: "#fff", border: "1px solid var(--mv-border)", borderRadius: 10,
            padding: 12, minWidth: 180, textAlign: "center",
          }}>
            {state.brandLogoUrl ? (
              <img src={state.brandLogoUrl} alt="Logo" data-testid="brand-logo-preview"
                style={{ maxWidth: 160, maxHeight: 70, objectFit: "contain" }} />
            ) : (
              <div className="text-small text-muted" data-testid="brand-logo-empty">
                Nessun logo caricato<br />(viene usato quello di default)
              </div>
            )}
          </div>
          <div style={{ flex: 1, minWidth: 260 }}>
            <label className="label">URL del logo</label>
            <input className="input" data-testid="brand-logo-url"
              placeholder="https://… (lascia vuoto per il logo di default)"
              value={state.brandLogoUrl}
              onChange={e => upd("brandLogoUrl", e.target.value)} />
            <div className="flex gap-2 mt-2" style={{ flexWrap: "wrap" }}>
              <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }}
                data-testid="brand-logo-file"
                onChange={e => uploadLogo(e.target.files?.[0])} />
              <button className="btn btn-secondary btn-sm" disabled={uploading}
                data-testid="brand-logo-upload-btn"
                onClick={() => fileRef.current?.click()}>
                {uploading ? <span className="spinner" /> : <><Upload size={14} /> Carica immagine</>}
              </button>
              {state.brandLogoUrl && (
                <button className="btn btn-ghost btn-sm" data-testid="brand-logo-clear"
                  onClick={() => upd("brandLogoUrl", "")}>
                  <RotateCcw size={14} /> Torna al logo di default
                </button>
              )}
            </div>
            <div className="text-small text-muted mt-2">
              PNG, JPG, WEBP, GIF o SVG · massimo 2 MB. Larghezza consigliata 400-600 px.
            </div>
          </div>
        </div>
      </div>

      {/* ---------------- CAMPI ---------------- */}
      <div className="grid gap-4 mb-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}>
        {FIELDS.map(([key, label, placeholder, tid]) => (
          <div key={key}>
            <label className="label">{label}</label>
            <input className="input" data-testid={tid} placeholder={placeholder}
              value={state[key] || ""} onChange={e => upd(key, e.target.value)} />
          </div>
        ))}
      </div>

      {/* ---------------- FIRMA ---------------- */}
      <label className="label">Firma delle email (HTML libero)</label>
      <textarea className="input" rows={6} data-testid="brand-signature"
        style={{ fontFamily: "monospace", fontSize: 12.5 }}
        value={state.brandSignatureHtml}
        onChange={e => upd("brandSignatureHtml", e.target.value)} />
      <div className="flex gap-2 items-center mt-2 mb-2">
        <button className="btn btn-ghost btn-sm" data-testid="brand-signature-reset"
          onClick={() => upd("brandSignatureHtml", DEFAULT_SIGNATURE)}>
          <RotateCcw size={14} /> Ripristina la firma di default
        </button>
      </div>
      <div className="card mb-4" style={{ padding: 14, background: "#fff" }}>
        <div className="text-small text-muted mb-2">Anteprima firma</div>
        <div data-testid="brand-signature-preview"
          style={{ fontSize: 13, lineHeight: 1.6, color: "#475569" }}
          dangerouslySetInnerHTML={{ __html: state.brandSignatureHtml }} />
      </div>

      {/* ---------------- AZIONI ---------------- */}
      <div className="flex gap-3 items-center mb-3" style={{ flexWrap: "wrap" }}>
        <button onClick={save} disabled={saving} className="btn btn-primary"
          data-testid="brand-save-btn">
          {saving ? <span className="spinner" /> : <><Save size={16} /> Salva branding</>}
        </button>
        <input className="input" style={{ maxWidth: 260 }} data-testid="brand-test-to"
          placeholder="tua@email.it" value={testTo}
          onChange={e => setTestTo(e.target.value)} />
        <button onClick={sendTest} disabled={sending || !testTo} className="btn btn-secondary"
          data-testid="brand-test-send-btn">
          {sending ? <span className="spinner" /> : <><Send size={16} /> Invia email di prova (T1)</>}
        </button>
      </div>
      <div className="text-small text-muted mb-2">
        L'email di prova salva prima le modifiche e invia la <strong>T1</strong> con logo,
        firma e recapiti attuali, usando i dati dell'ultima analisi completata.
      </div>
      {msg && (
        <span className={`badge ${msg.ok ? "badge-green" : "badge-red"}`}
          data-testid="brand-settings-msg">{msg.text}</span>
      )}
    </div>
  );
}
